import { useInfiniteQuery, useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";

import { useNavigate } from "react-router-dom";

import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import type { Subscription } from "@/@types/subscriptions";
import { removeLoginInfo } from "@/api/tokens";
import UserRequests from "@/api/user/user.requests.ts";
import type { CreateProfile, OrderParams, OrderType, PatchProfile } from "@/api/user/user.types.ts";
import { useLang } from "@/hooks/useLang.ts";

import useModalRouter from "../useModalRouter";

export const USER_KEYS = {
  loginCode: ["login-code"],
  profileDetail: (id: number) => ["profile-detail", id],
  profilePicturesCategory: ["profile-pictures-category"],
  notifications: (lang: string, limit: number) => ["notifications", lang, limit],
  notificationsCount: (lang: string) => ["notifications-count", lang],
  notificationDetail: (lang: string, id: number) => ["notification-detail", lang, id],
  userDetail: (lang: string) => ["user-detail", lang],
  creditCards: (lang: string) => ["credit-cards", lang],
  subscriptions: (lang: string) => ["subscriptions", lang],
  sessions: (lang: string) => ["sessions", lang],
  movieOrders: (lang: string) => ["movie-orders", lang],
  orders: (lang: string, orderType?: OrderType) => ["orders", lang, orderType],
  createCode: (lang: string) => ["create-code", lang],
  order: (lang: string, id: number) => ["order", lang, id],
  paymentMethods: (lang: string, id: number) => ["payment-methods", lang, id],
};

export const useGetLoginCode = () => {
  return useSuspenseQuery({
    queryKey: USER_KEYS.loginCode,
    queryFn: () => UserRequests.getLoginCode(),
    staleTime: 1000 * 30,
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lang = useLang();
  const { openModal } = useModalRouter();

  return useMutation({
    mutationFn: () => UserRequests.postLogout(),
    onSettled: () => {
      removeLoginInfo();
      navigate("/");
      openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_LOGIN]);
      queryClient.invalidateQueries({ queryKey: USER_KEYS.userDetail(lang) });
    },
  });
};

export const useGetProfileDetail = (id: number) => {
  return useSuspenseQuery({
    queryKey: USER_KEYS.profileDetail(id),
    queryFn: () => UserRequests.getProfileDetail(id),
    staleTime: 0,
  });
};

export const useGetProfilePicturesCategory = () => {
  return useSuspenseQuery({
    queryKey: USER_KEYS.profilePicturesCategory,
    queryFn: () => UserRequests.getProfilePicturesCategory(),
    staleTime: 1000 * 60 * 60,
  });
};

export const usePatchProfile = () => {
  return useMutation({
    mutationFn: ({ user_id, data }: { user_id: number; data: PatchProfile }) =>
      UserRequests.patchProfile(user_id, data),
  });
};

export const useCreatePostProfile = () => {
  return useMutation({
    mutationFn: ({ data }: { data: CreateProfile }) => UserRequests.createPostProfile(data),
  });
};
export const useDestroyProfile = () => {
  return useMutation({
    mutationFn: ({ id }: { id: number }) => UserRequests.destroyProfile(id),
  });
};

export const useNotificationsList = (limit: number) => {
  const lang = useLang();

  return useInfiniteQuery({
    queryKey: USER_KEYS.notifications(lang, limit),
    queryFn: ({ pageParam = 0 }) => UserRequests.getNotifications(limit, pageParam * limit),
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.next ? allPages.length : undefined;
    },
    initialPageParam: 0,
    staleTime: 1000 * 60 * 15,
  });
};

export const useNotificationsCount = () => {
  const lang = useLang();
  return useQuery({
    queryKey: USER_KEYS.notificationsCount(lang),
    queryFn: () => UserRequests.getNotificationsCount(),
    staleTime: 1000 * 60 * 5,
  });
};

export const useNotificationDetail = (id: number) => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: USER_KEYS.notificationDetail(lang, id),
    queryFn: () => UserRequests.getNotificationDetail(id),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetUser = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: USER_KEYS.userDetail(lang),
    queryFn: () => UserRequests.getUserDetail(),
    staleTime: 1000 * 60 * 15,
  });
};

export const useCheckPinCode = () => {
  return useMutation({
    mutationFn: ({ formData }: { formData: FormData }) => UserRequests.postCheckPinCode({ formData }),
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: ({ formData }: { formData: FormData }) => UserRequests.postChangePassword({ formData }),
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: ({ formData }: { formData: FormData }) => UserRequests.postResetPassword({ formData }),
  });
};

export const useGetCreditCards = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: USER_KEYS.creditCards(lang),
    queryFn: () => UserRequests.getCreditCards(),
    staleTime: 1000 * 60 * 15,
  });
};

export const useUpdateCreditCard = (id: number) => {
  return useMutation({
    mutationFn: ({ formData }: { formData: FormData }) => UserRequests.putCreditCard({ formData, id }),
  });
};

export const useDeleteCreditCard = () => {
  return useMutation({
    mutationFn: (id: number) => UserRequests.deleteCreditCard({ id }),
  });
};

export const useDeleteAccountSendCode = () => {
  return useMutation({
    mutationFn: () => UserRequests.deleteAccountSendCode(),
  });
};

export const useDeleteAccount = () => {
  return useMutation({
    mutationFn: (code: string) => UserRequests.deleteAccount(code),
  });
};

export const useAddCreditCardInit = () => {
  return useMutation({
    mutationFn: ({ ...params }: { card_number: string; expire_date: string }) =>
      UserRequests.addCreditCardInit({ ...params }),
  });
};

export const useAddCreditCardConfirm = () => {
  return useMutation({
    mutationFn: ({ ...params }: { transaction_id: string; otp: string }) =>
      UserRequests.addCreditCardConfirm({ ...params }),
  });
};

export const useGetSubscriptions = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: USER_KEYS.subscriptions(lang),
    queryFn: () => UserRequests.getSubscriptions(),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetSubscription = (id: number): Subscription | undefined => {
  const { data } = useGetSubscriptions();

  return data.find((item) => item.id == id);
};

export const useGetSessions = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: USER_KEYS.sessions(lang),
    queryFn: () => UserRequests.getSessions(),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetMovieOrders = () => {
  const lang = useLang();

  return useSuspenseQuery({
    queryKey: USER_KEYS.movieOrders(lang),
    queryFn: () => UserRequests.getMovieOrders(),
    staleTime: 1000 * 60 * 15,
  });
};

export const useGetOrders = ({ orderType }: { orderType?: OrderType }) => {
  const lang = useLang();

  return useQuery({
    queryKey: USER_KEYS.orders(lang, orderType),
    queryFn: () => UserRequests.getOrders({ orderType }),
    staleTime: 1000 * 60 * 15,
  });
};

export const useCreateLoginCode = () => {
  const lang = useLang();

  return useQuery({
    queryKey: USER_KEYS.createCode(lang),
    queryFn: () => UserRequests.createLoginCode(),
    staleTime: 1000 * 60 * 1,
  });
};

export const useGetOrder = (id: number) => {
  const lang = useLang();

  return useQuery({
    queryKey: USER_KEYS.order(lang, id),
    queryFn: () => UserRequests.getOrder(id),
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetPaymentMethods = (id: number) => {
  const lang = useLang();

  return useQuery({
    queryKey: USER_KEYS.paymentMethods(lang, id),
    queryFn: () => UserRequests.getPaymentMethods(id),
    staleTime: 1000 * 60 * 1,
  });
};

export const useConfirmLoginCode = () => {
  return useMutation({
    mutationFn: (code: string) => UserRequests.loginCodeConfirm(code),
  });
};

export const useDeleteSession = () => {
  return useMutation({
    mutationFn: (myKey: string) => UserRequests.deleteSession(myKey),
  });
};

export const useDeleteAllSessions = () => {
  return useMutation({
    mutationFn: () => UserRequests.deleteAllSessions(),
  });
};

export const useCreateOrder = () => {
  return useMutation({
    mutationFn: ({ ...data }: OrderParams) => UserRequests.createOrder(data),
  });
};

export const useCreateOrderAndPerform = () => {
  return useMutation({ mutationFn: ({ ...data }: OrderParams) => UserRequests.createOrderAndPerform(data) });
};

export const usePerformBonusPayment = () => {
  return useMutation({ mutationFn: ({ ...data }: { bank_card_id: number }) => UserRequests.performBonusPayment(data) });
};

export const useChangeSubsAutoPayment = () => {
  return useMutation({
    mutationFn: ({ ...data }: { subscription_id: number; auto_payment: boolean; reason?: string }) =>
      UserRequests.changeSubsAutoPayment(data),
  });
};

export const useOrderMovie = () => {
  return useMutation({
    mutationFn: ({ ...data }: { content_name: string; content_description?: string; content_link?: string }) =>
      UserRequests.orderMovie(data),
  });
};
